/**
 * useCanvasNavigation — Auto-zoom navigation for the process canvas.
 *
 * Provides:
 * - focusOnNode(nodeId) — zoom + center on a specific node and its connections
 * - focusOnArea(sectionName) — zoom to show an entire section cluster
 * - resetView() — zoom out to show the full canvas
 *
 * Queues navigation requests while the user is actively panning/zooming,
 * and executes them 500ms after the user interaction ends.
 *
 * Must be used inside a ReactFlowProvider.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useReactFlow } from '@xyflow/react';
import type { CanvasFlowNode } from './useProcessCanvas';
import type { Edge } from '@xyflow/react';

export interface AgentFocusRequest {
  type: 'node' | 'area';
  id: string;
}

export interface UseCanvasNavigationReturn {
  focusOnNode: (nodeId: string) => void;
  focusOnArea: (areaId: string) => void;
  resetView: () => void;
  handleFocusRequest: (request: AgentFocusRequest) => void;
  /** The node ID currently being focus-pulsed (for visual indicator) */
  focusedNodeId: string | null;
  /** Whether a navigation is queued because user is interacting */
  isNavigationPending: boolean;
}

const USER_INTERACTION_DELAY_MS = 500;
const FOCUS_PULSE_DURATION_MS = 1200; // 2 pulses at ~600ms each

export function useCanvasNavigation(
  nodes: CanvasFlowNode[],
  edges: Edge[],
): UseCanvasNavigationReturn {
  const { fitView } = useReactFlow();

  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);
  const [isNavigationPending, setIsNavigationPending] = useState(false);

  // Track user interaction state
  const isUserInteractingRef = useRef(false);
  const interactionEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queuedActionRef = useRef<(() => void) | null>(null);
  const pulseClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep latest nodes/edges in refs for callbacks
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { edgesRef.current = edges; }, [edges]);

  // ── User interaction tracking ──────────────────────────────────────────

  useEffect(() => {
    function onPointerDown() {
      isUserInteractingRef.current = true;
      if (interactionEndTimerRef.current) {
        clearTimeout(interactionEndTimerRef.current);
        interactionEndTimerRef.current = null;
      }
    }

    function onPointerUp() {
      interactionEndTimerRef.current = setTimeout(() => {
        isUserInteractingRef.current = false;
        // Execute queued navigation
        if (queuedActionRef.current) {
          const action = queuedActionRef.current;
          queuedActionRef.current = null;
          setIsNavigationPending(false);
          action();
        }
      }, USER_INTERACTION_DELAY_MS);
    }

    // Listen on the react-flow container
    const container = document.querySelector('.react-flow');
    if (container) {
      container.addEventListener('pointerdown', onPointerDown);
      container.addEventListener('pointerup', onPointerUp);
    }

    return () => {
      if (container) {
        container.removeEventListener('pointerdown', onPointerDown);
        container.removeEventListener('pointerup', onPointerUp);
      }
      if (interactionEndTimerRef.current) clearTimeout(interactionEndTimerRef.current);
    };
  }, []);

  // Clean up pulse timer on unmount
  useEffect(() => {
    return () => {
      if (pulseClearTimerRef.current) clearTimeout(pulseClearTimerRef.current);
    };
  }, []);

  // ── Execute or queue a navigation action ───────────────────────────────

  const executeOrQueue = useCallback((action: () => void) => {
    if (isUserInteractingRef.current) {
      // Replace any previously queued action
      queuedActionRef.current = action;
      setIsNavigationPending(true);
    } else {
      action();
    }
  }, []);

  // ── Pulse a node after zoom completes ──────────────────────────────────

  const pulseNode = useCallback((nodeId: string) => {
    setFocusedNodeId(nodeId);
    if (pulseClearTimerRef.current) clearTimeout(pulseClearTimerRef.current);
    pulseClearTimerRef.current = setTimeout(() => {
      setFocusedNodeId(null);
    }, FOCUS_PULSE_DURATION_MS);
  }, []);

  // ── Focus on a single node + its immediate connections ─────────────────

  const focusOnNode = useCallback((nodeId: string) => {
    executeOrQueue(() => {
      const currentNodes = nodesRef.current;
      const currentEdges = edgesRef.current;

      // Find the target node and its immediate neighbors
      const connectedIds = new Set<string>([nodeId]);
      for (const edge of currentEdges) {
        if (edge.source === nodeId) connectedIds.add(edge.target);
        if (edge.target === nodeId) connectedIds.add(edge.source);
      }

      // Filter to node IDs that actually exist
      const existingIds = new Set(currentNodes.map((n) => n.id));
      const nodeIds = [...connectedIds].filter((id) => existingIds.has(id));

      if (nodeIds.length === 0) return;

      fitView({
        nodes: nodeIds.map((id) => ({ id })),
        duration: 300,
        padding: 0.4,
        maxZoom: 1.5,
      });

      // Pulse the target node after the zoom animation completes
      setTimeout(() => pulseNode(nodeId), 320);
    });
  }, [executeOrQueue, fitView, pulseNode]);

  // ── Focus on an area (section cluster) ─────────────────────────────────

  const focusOnArea = useCallback((areaId: string) => {
    executeOrQueue(() => {
      const currentNodes = nodesRef.current;

      // Find all nodes in the section
      const sectionNodes = currentNodes.filter(
        (n) => n.data.section === areaId,
      );

      if (sectionNodes.length === 0) return;

      fitView({
        nodes: sectionNodes.map((n) => ({ id: n.id })),
        duration: 400,
        padding: 0.3,
        maxZoom: 1.2,
      });
    });
  }, [executeOrQueue, fitView]);

  // ── Reset to full canvas view ──────────────────────────────────────────

  const resetView = useCallback(() => {
    executeOrQueue(() => {
      fitView({ duration: 400, padding: 0.3 });
    });
  }, [executeOrQueue, fitView]);

  // ── Handle agent focus requests ────────────────────────────────────────

  const handleFocusRequest = useCallback((request: AgentFocusRequest) => {
    if (request.type === 'node') {
      focusOnNode(request.id);
    } else if (request.type === 'area') {
      focusOnArea(request.id);
    }
  }, [focusOnNode, focusOnArea]);

  return {
    focusOnNode,
    focusOnArea,
    resetView,
    handleFocusRequest,
    focusedNodeId,
    isNavigationPending,
  };
}
